import React, { createContext, useContext, useState, ReactNode } from 'react';

type Language = 'fr' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  fr: {
    // Header navigation
    'nav.about': 'Qui sommes nous',
    'nav.services': 'Nos services',
    'nav.partners': 'Nos Partenaires',
    'nav.news': 'Actualité et événement',
    'nav.contact': 'Contacter nous',
    // Accueil (Index)
    'home.hero.title': 'Leader ProMed',
    'home.hero.subtitle': 'Votre partenaire de confiance pour la promotion médicale en Mauritanie',
    'home.hero.cta': 'Découvrir Nos Services',
    'home.cards.existence.cta': 'En Savoir Plus',
    'home.cards.partnership.cta': 'Voir Partenaires',
    'home.cards.promotion.cta': 'Nos Services',
    'home.cards.existence.title': 'Notre Expertise',
    'home.cards.existence.desc': "Plus de 8 ans d'expérience dans le domaine de la promotion médicale en Mauritanie, avec une équipe de professionnels dévoués.",
    'home.cards.partnership.title': 'Nos Partenaires',
    'home.cards.partnership.desc': "Nous collaborons avec les meilleurs laboratoires internationaux pour apporter des solutions médicales innovantes en Mauritanie.",
    'home.cards.promotion.title': 'Promotion Médicale',
    'home.cards.promotion.desc': "Des stratégies de promotion efficaces pour les produits médicaux, adaptées au marché mauritanien et aux besoins locaux.",
    'home.whoWeAre.description.part1': 'agence de développement des investissements pharmaceutiques',
    'home.whoWeAre.description.part2': 'laboratoires pharmaceutiques internationaux',
    'home.whoWeAre.description.part3': 'services personnalisés',
    'home.whoWeAre.cta': 'En Savoir Plus Sur Nous',
    'home.gallery.cta': 'Voir Toute La Galerie',
    'home.contact.form.name': 'Nom',
    'home.contact.form.email': 'Email',
    'home.contact.form.subject': 'Sujet',
    'home.contact.form.message': 'Message',
    'home.contact.form.cta': 'Envoyer Message',
    'home.contact.address': 'ZRB NO 0304, Nouakchott, Mauritanie',
    'home.contact.email': 'contact@leaderpromed.com',
    'home.contact.phone': '+222 41 41 47 67',
    // Services
    'services.title': 'Nos Services',
    'services.subtitle': 'Des solutions complètes pour le secteur pharmaceutique en Mauritanie',
    'services.detailsTitle': 'Nos prestations incluent :',
    'services.seeLess': 'Voir moins',
    'services.learnMore': 'En Savoir Plus',
    'services.whyChooseUsTitle': 'Pourquoi Nous Choisir ?',
    'services.whyChooseUsDescription': 'Notre expertise et notre engagement font de nous le partenaire idéal pour le développement de vos activités en Mauritanie.',
    'services.expertiseLocaleTitle': 'Expertise Locale',
    'services.expertiseLocaleDescription': 'Une connaissance approfondie du marché pharmaceutique mauritanien et des réglementations en vigueur.',
    'services.extendedNetworkTitle': 'Réseau Étendu',
    'services.extendedNetworkDescription': 'Un réseau solide de partenaires et de professionnels de santé à travers le pays.',
    'services.qualityGuaranteeTitle': 'Qualité Garantie',
    'services.qualityGuaranteeDescription': 'Un engagement fort pour la qualité et la conformité dans tous nos services.',
    // Actualités
    'news.title': 'Actualités & Événements',
    'news.subtitle': 'Restez informés sur nos dernières actualités et événements dans le secteur médical',
    'newsletter.title': 'Restez Informé',
    'newsletter.subtitle': 'Inscrivez-vous à notre newsletter pour recevoir nos dernières actualités et événements directement dans votre boîte mail.',
    'newsletter.placeholder': 'Votre adresse email',
    'newsletter.button': 'S\'inscrire',
    // Contact
    'contact.title': 'Contactez-Nous',
    'contact.subtitle': 'Nous sommes toujours disponibles pour répondre à vos questions et besoins.',
    'contact.form.title': 'Envoyez-nous un message',
    'contact.form.name': 'Nom',
    'contact.form.namePlaceholder': 'Votre nom',
    'contact.form.email': 'Email',
    'contact.form.emailPlaceholder': 'Votre email',
    'contact.form.subject': 'Sujet',
    'contact.form.subjectPlaceholder': 'Sujet de votre message',
    'contact.form.message': 'Message',
    'contact.form.messagePlaceholder': 'Votre message',
    'contact.form.submit': 'Envoyer Message',
    'contact.info.title': 'Informations de contact',
    'contact.info.address': 'Adresse',
    'contact.info.addressDetails': 'ZRB NO 0304, Nouakchott, Mauritanie',
    'contact.info.email': 'Email',
    'contact.info.emailDetails': 'contact@leaderpromed.com',
    'contact.info.phone': 'Téléphone',
    'contact.info.phoneDetails': '+222 41 41 47 67',
    'contact.info.follow': 'Suivez-nous',
    
    // About page
    'about.title': 'Qui sommes-nous ?',
    'about.subtitle': 'Leader ProMed (LPM) est une agence de développement des investissements pharmaceutiques en Mauritanie. Elle agit comme un intermédiaire stratégique entre les laboratoires pharmaceutiques internationaux et les grossistes agréés en Mauritanie, tout en facilitant les relations avec le ministère de la Santé. Grâce à son expertise et son ancrage local, LPM joue un rôle essentiel dans la chaîne de mise à disposition des médicaments sur le marché mauritanien.',
    'about.ceo.title': 'Notre Directeur Général',
    'about.ceo.p1': 'Dr. Ahmed Aly est un professionnel de santé reconnu pour son engagement dans le développement du secteur pharmaceutique en Mauritanie. Fort de son expérience dans les hôpitaux et dans la gestion des projets de santé, il dirige aujourd\'hui Leader ProMed avec une vision stratégique axée sur l\'innovation et le partenariat durable. Sa passion pour l\'amélioration des soins de santé en Mauritanie et son expertise dans le domaine pharmaceutique font de lui un leader respecté dans le secteur. Sous sa direction, Leader ProMed s\'est imposée comme un acteur majeur dans la promotion et la distribution de produits pharmaceutiques innovants, contribuant ainsi significativement à l\'amélioration de l\'accès aux soins de qualité en Mauritanie.',
    'about.vision.title': 'Notre Vision',
    'about.vision.p1': 'Depuis sa création en 2017, LPM a connu une croissance remarquable grâce à une vision stratégique claire, une équipe dévouée et un engagement constant envers la qualité des services offerts.',
    'about.vision.p2': 'À travers cette démarche, nous visons à apporter une valeur ajoutée significative à la fois pour les patients, les professionnels de santé, nos partenaires et l\'ensemble du système de santé Mauritanien.' ,
    'about.history.title': 'Notre Histoire',
    'about.history.p1': 'Depuis sa création en 2017, LPM a connu une croissance remarquable grâce à une vision stratégique claire, une équipe dévouée et un engagement constant envers la qualité des services offerts. L agence a progressivement mis en place une organisation structurée avec un réseau actif de visiteurs médicaux couvrant l\'ensemble du territoire mauritanien. Elle a noué des partenariats avec des laboratoires de renom, assurant la représentation de produits innovants et essentiels pour le système de santé national.', 
    'about.history.p2': 'Grâce à son positionnement en tant qu\'intermédiaire fiable entre les laboratoires internationaux, les grossistes et les autorités sanitaires, LPM s\'est imposée comme un acteur clé du secteur pharmaceutique. Sa capacité à accompagner les dossiers réglementaires, à promouvoir les produits avec rigueur scientifique et à garantir un suivi terrain efficace, constitue aujourd\'hui sa principale force.',
    'about.history.p3': 'Aujourd\'hui, Leader ProMed est reconnue pour son professionnalisme, son dynamisme et son rôle central dans le développement des investissements pharmaceutiques en Mauritanie.',
    'about.values.title': 'Nos Valeurs',
    'about.excellence.title': 'Excellence',
    'about.excellence.desc': 'Nous nous engageons à maintenir les plus hauts standards de qualité dans tous nos produits et services.',
    'about.innovation.title': 'Innovation',
    'about.innovation.desc': 'Nous restons à la pointe de la technologie médicale pour offrir les solutions les plus avancées.',
    'about.trust.title': 'Confiance',
    'about.trust.desc': 'Nous construisons des relations durables basées sur la transparence et la fiabilité.',
    'about.team.title': 'Notre Équipe',
    'about.team.p1': 'Le succès de Leader ProMed repose avant tout sur la force de son équipe. Derrière chaque partenariat, chaque produit promu, chaque dossier accompagné, se trouve un collectif de professionnels engagés, compétents et passionnés par l\'innovation en santé.',
    
    // Services page
    'services.medical.title': 'Équipements Médicaux',
    'services.medical.desc': 'Large gamme d\'équipements médicaux de dernière génération pour tous types d\'établissements de santé.',
    'services.cardiology.title': 'Cardiologie',
    'services.cardiology.desc': 'Solutions spécialisées pour le diagnostic et le traitement des pathologies cardiovasculaires.',
    'services.maintenance.title': 'Maintenance & Support',
    'services.maintenance.desc': 'Service après-vente complet avec maintenance préventive et corrective de vos équipements.',
    'services.innovation.title': 'Solutions Innovantes',
    'services.innovation.desc': 'Technologies de pointe pour améliorer l\'efficacité et la qualité des soins.',
    'services.process.title': 'Notre Processus',
    'services.consultation': 'Consultation',
    'services.consultation.desc': 'Analyse de vos besoins spécifiques',
    'services.proposition': 'Proposition',
    'services.proposition.desc': 'Solutions personnalisées et devis détaillé',
    'services.installation': 'Installation',
    'services.installation.desc': 'Mise en place et configuration',
    'services.support': 'Support',
    'services.support.desc': 'Accompagnement et maintenance continue',
    'services.learn.more': 'En savoir plus',
    // Services cards
    'services.card1.title': 'Représentation de Laboratoires',
    'services.card1.description': 'Nous représentons des laboratoires pharmaceutiques internationaux en Mauritanie, assurant une présence professionnelle et efficace sur le marché local.',
    'services.card1.detail1': 'Représentation exclusive de laboratoires internationaux',
    'services.card1.detail2': 'Gestion des relations avec les autorités locales',
    'services.card1.detail3': 'Suivi des activités commerciales',
    'services.card1.detail4': 'Organisation d\'événements scientifiques',
    'services.card1.detail5': 'Formation des équipes de promotion',
    'services.card2.title': 'Promotion de Médicaments',
    'services.card2.description': 'Notre équipe de promotion médicale assure une présence active auprès des professionnels de santé pour promouvoir les produits de nos partenaires.',
    'services.card2.detail1': 'Visites médicales régulières',
    'services.card2.detail2': 'Organisation de réunions scientifiques',
    'services.card2.detail3': 'Formation continue des professionnels de santé',
    'services.card2.detail4': 'Suivi des prescriptions',
    'services.card2.detail5': 'Analyse des tendances du marché',
    'services.card3.title': 'Enregistrement des Médicaments',
    'services.card3.description': 'Nous accompagnons et suivons l\'enregistrement des dossiers des médicaments pour l\'obtention du CLV ou de l\'AMM auprès des autorités compétentes.',
    'services.card3.detail1': 'Préparation des dossiers d\'enregistrement',
    'services.card3.detail2': 'Suivi administratif complet',
    'services.card3.detail3': 'Coordination avec les autorités sanitaires',
    'services.card3.detail4': 'Gestion des renouvellements',
    'services.card3.detail5': 'Conseil réglementaire',
    'services.card4.title': 'Pharmacovigilance',
    'services.card4.description': 'Nous gérons la pharmacovigilance pour assurer la sécurité des médicaments et le suivi des effets indésirables potentiels.',
    'services.card4.detail1': 'Collecte et analyse des effets indésirables',
    'services.card4.detail2': 'Rapports périodiques de sécurité',
    'services.card4.detail3': 'Gestion des alertes sanitaires',
    'services.card4.detail4': 'Formation à la pharmacovigilance',
    'services.card4.detail5': 'Conformité réglementaire',
    'services.card5.title': 'Gestion des Appels d\'Offres',
    'services.card5.description': 'Nous accompagnons nos partenaires dans la gestion et la réponse aux appels d\'offres en Mauritanie, avec une expertise du marché local.',
    'services.card5.detail1': 'Veille des appels d\'offres',
    'services.card5.detail2': 'Préparation des dossiers de réponse',
    'services.card5.detail3': 'Analyse des cahiers des charges',
    'services.card5.detail4': 'Négociation des contrats',
    'services.card5.detail5': 'Suivi des marchés',
    'services.card6.title': 'Consultation Investissement',
    'services.card6.description': 'Nous offrons des services de consultation pour les laboratoires intéressés par l\'investissement en Mauritanie, avec une expertise du marché local.',
    'services.card6.detail1': 'Étude de marché détaillée',
    'services.card6.detail2': 'Analyse de la réglementation',
    'services.card6.detail3': 'Identification des opportunités',
    'services.card6.detail4': 'Stratégie d\'entrée sur le marché',
    'services.card6.detail5': 'Accompagnement à l\'implantation',
    'services.card7.title': 'Équipement Médical',
    'services.card7.description': 'Nous fournissons des équipements médicaux de haute qualité pour les établissements de santé en Mauritanie.',
    'services.card7.detail1': 'Sélection d\'équipements de qualité',
    'services.card7.detail2': 'Installation et formation',
    'services.card7.detail3': 'Maintenance préventive',
    'services.card7.detail4': 'Support technique',
    'services.card7.detail5': 'Mise à niveau des équipements',
    // Footer
    'footer.description': 'Votre partenaire de confiance pour la promotion médicale en Mauritanie depuis 2017.',
    'footer.quickLinks': 'Liens Rapides',
    'footer.link.home': 'Accueil',
    'footer.link.about': 'À Propos',
    'footer.link.services': 'Services',
    'footer.link.partners': 'Partenaires',
    'footer.link.news': 'Actualités',
    'footer.link.contact': 'Contact',
    'footer.contact': 'Contact',
    'footer.address': 'ZRB NO 0304, Nouakchott, Mauritanie',
    'footer.phone': '+222 41 41 47 67',
    'footer.email': 'contact@leaderpromed.com',
    'footer.rights': 'Tous droits réservés.',
    // Home partners
    'home.partners.title': 'Nos Partenaires',
    'home.partners.desc': 'Nous travaillons avec les laboratoires pharmaceutiques internationaux les plus réputés pour offrir des produits de qualité au marché mauritanien.',
    // Home news
    'home.news.title': 'Actualités & Événements',
    'home.news.desc': 'Restez informés sur nos dernières actualités et événements dans le secteur médical.',
    'home.news.card1.title': "Réunion scientifique sur le pied diabétique",
    'home.news.card1.content': "L'Association Mauritanienne des Maladies Endocriniennes, du Diabète et de la Nutrition a organisé une réunion scientifique intitulée 'Le pied diabétique : de la prévention au traitement des complications'. Cette conférence a réuni des experts médicaux pour discuter des meilleures pratiques de prévention et de traitement du pied diabétique en Mauritanie.",
    'home.news.card2.title': "4e Congrès Maghrébin de Chirurgie Orthopédique",
    'home.news.card2.content': "Nous partageons avec vous quelques moments du premier jour du 4e Congrès Maghrébin - Congrès National de Chirurgie Orthopédique et Traumatologique, organisé par la Société Mauritanienne de la Chirurgie Orthopédique et Traumatologique. Cet événement majeur rassemble les meilleurs spécialistes du Maghreb pour échanger sur les dernières avancées en chirurgie orthopédique.",
    'home.news.card3.title': "2e Congrès National des Médecins Mauritaniens",
    'home.news.card3.content': "Les moments forts du 2e Congrès National de l'Ordre National des Médecins Mauritaniens, qui s'est tenu hier et se poursuit aujourd'hui, ont été capturés afin de conserver une trace vivante de cette rencontre majeure. Cet événement rassemble l'élite du corps médical mauritanien pour discuter des enjeux actuels de la profession.",
    'home.news.cta': 'Toutes Les Actualités',
    // Home gallery
    'home.gallery.title': 'Galerie',
    'home.gallery.desc': 'Découvrez nos événements, formations et activités en images.',
    'home.gallery.slide1.title': "Congrès d'Orthopédie - Session 1",
    'home.gallery.slide1.date': 'Mars 2024',
    'home.gallery.slide2.title': "Congrès d'Orthopédie - Session 2",
    'home.gallery.slide2.date': 'Mars 2024',
    'home.gallery.slide3.title': "Congrès d'Orthopédie - Session 3",
    'home.gallery.slide3.date': 'Mars 2024',
    'home.gallery.slide4.title': "Congrès SOMEDIAN - Session 1",
    'home.gallery.slide4.date': 'Mars 2024',
    'home.gallery.slide5.title': "Congrès SOMEDIAN - Session 2",
    'home.gallery.slide5.date': 'Mars 2024',
    'home.gallery.slide6.title': "Congrès SOMEDIAN - Session 3",
    'home.gallery.slide6.date': 'Mars 2024',
    // Home contact
    'home.contact.title': 'Contactez-Nous',
    'home.contact.subtitle': 'Nous sommes toujours disponibles pour répondre à vos questions et besoins.',
    'home.contact.form.title': 'Envoyez-nous un message',
    'home.contact.info.title': 'Informations de contact',
    'home.contact.info.address': 'Adresse',
    'home.contact.info.email': 'Email',
    'home.contact.info.phone': 'Téléphone',
    'home.contact.info.follow': 'Suivez-nous',
    'home.whoWeAre.title': 'Qui Nous Sommes',
    'home.whoWeAre.p1': "Leader ProMed (LPM) est une <about>agence de développement des investissements pharmaceutiques</about> en Mauritanie. Elle agit comme un intermédiaire stratégique entre les <partners>laboratoires pharmaceutiques internationaux</partners> et les grossistes agréés en Mauritanie, tout en facilitant les relations avec le ministère de la Santé.",
    'home.whoWeAre.p2': "Avec une équipe de professionnels qualifiés et une connaissance approfondie du secteur de la santé en Mauritanie, nous offrons des <services>services personnalisés</services> pour répondre aux besoins spécifiques de nos partenaires et contribuer à l'amélioration des soins de santé dans le pays.",
  },
  en: {
    // Header navigation
    'nav.about': 'About Us',
    'nav.services': 'Our Services',
    'nav.partners': 'Our Partners',
    'nav.news': 'News & Events',
    'nav.contact': 'Contact Us',
    // Home (Index)
    'home.hero.title': 'Leader ProMed',
    'home.hero.subtitle': 'Your trusted partner for medical promotion in Mauritania',
    'home.hero.cta': 'Discover Our Services',
    'home.cards.existence.cta': 'Learn More',
    'home.cards.partnership.cta': 'See Partners',
    'home.cards.promotion.cta': 'Our Services',
    'home.cards.existence.title': 'Our Expertise',
    'home.cards.existence.desc': "Over 8 years of experience in medical promotion in Mauritania, with a dedicated team of professionals.",
    'home.cards.partnership.title': 'Our Partners',
    'home.cards.partnership.desc': "We collaborate with top international laboratories to bring innovative medical solutions to Mauritania.",
    'home.cards.promotion.title': 'Medical Promotion',
    'home.cards.promotion.desc': "Effective promotion strategies for medical products, tailored to the Mauritanian market and local needs.",
    // Home (Index)
    'home.whoWeAre.description.part1': 'pharmaceutical investment development agency',
    'home.whoWeAre.description.part2': 'international pharmaceutical laboratories',
    'home.whoWeAre.description.part3': 'customized services',
    'home.whoWeAre.cta': 'Learn More About Us',
    'home.gallery.cta': 'See Full Gallery',
    'home.contact.form.name': 'Name',
    'home.contact.form.email': 'Email',
    'home.contact.form.subject': 'Subject',
    'home.contact.form.message': 'Message',
    'home.contact.form.cta': 'Send Message',
    'home.contact.address': 'ZRB NO 0304, Nouakchott, Mauritania',
    'home.contact.email': 'contact@leaderpromed.com',
    'home.contact.phone': '+222 41 41 47 67',
    // Services
    'services.title': 'Our Services',
    'services.subtitle': 'Comprehensive solutions for the pharmaceutical sector in Mauritania',
    'services.detailsTitle': 'Our services include:',
    'services.seeLess': 'See less',
    'services.learnMore': 'Learn More',
    'services.whyChooseUsTitle': 'Why Choose Us?',
    'services.whyChooseUsDescription': 'Our expertise and commitment make us the ideal partner for the development of your activities in Mauritania.',
    'services.expertiseLocaleTitle': 'Local Expertise',
    'services.expertiseLocaleDescription': 'In-depth knowledge of the Mauritanian pharmaceutical market and current regulations.',
    'services.extendedNetworkTitle': 'Extended Network',
    'services.extendedNetworkDescription': 'A strong network of partners and healthcare professionals across the country.',
    'services.qualityGuaranteeTitle': 'Quality Guarantee',
    'services.qualityGuaranteeDescription': 'A strong commitment to quality and compliance in all our services.',
    // News
    'news.title': 'News & Events',
    'news.subtitle': 'Stay informed about our latest news and events in the medical sector',
    'newsletter.title': 'Stay Informed',
    'newsletter.subtitle': 'Subscribe to our newsletter to receive our latest news and events directly in your inbox.',
    'newsletter.placeholder': 'Your email address',
    'newsletter.button': 'Subscribe',
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'We are always available to answer your questions and needs.',
    'contact.form.title': 'Send us a message',
    'contact.form.name': 'Name',
    'contact.form.namePlaceholder': 'Your name',
    'contact.form.email': 'Email',
    'contact.form.emailPlaceholder': 'Your email',
    'contact.form.subject': 'Subject',
    'contact.form.subjectPlaceholder': 'Subject of your message',
    'contact.form.message': 'Message',
    'contact.form.messagePlaceholder': 'Your message',
    'contact.form.submit': 'Send Message',
    'contact.info.title': 'Contact Information',
    'contact.info.address': 'Address',
    'contact.info.addressDetails': 'ZRB NO 0304, Nouakchott, Mauritania',
    'contact.info.email': 'Email',
    'contact.info.emailDetails': 'contact@leaderpromed.com',
    'contact.info.phone': 'Phone',
    'contact.info.phoneDetails': '+222 41 41 47 67',
    'contact.info.follow': 'Follow us',
    
    // About page
    'about.title': 'About Us',
    'about.subtitle': 'Leader ProMed (LPM) is a pharmaceutical investment development agency in Mauritania. It acts as a strategic intermediary between <partners>international pharmaceutical laboratories</partners> and authorized wholesalers in Mauritania, while facilitating relations with the Ministry of Health.',
    'about.ceo.title': 'Our CEO',
    'about.ceo.p1': 'Dr. Ahmed Aly is a recognized healthcare professional for his commitment to the development of the pharmaceutical sector in Mauritania. With his extensive experience in hospitals and health project management, he now leads Leader ProMed with a strategic vision focused on innovation and sustainable partnership. His passion for improving healthcare in Mauritania and his expertise in the pharmaceutical field make him a respected leader in the sector. Under his leadership, Leader ProMed has established itself as a major player in the promotion and distribution of innovative pharmaceutical products, thus significantly contributing to improving access to quality healthcare in Mauritania.',
    'about.vision.title': 'Our Vision',
    'about.vision.p1': 'Since its creation in 2017, LPM has experienced remarkable growth thanks to a clear strategic vision, a dedicated team, and a constant commitment to the quality of services offered.',
    'about.vision.p2': 'Through this approach, we aim to bring significant added value to patients, healthcare professionals, our partners, and the entire Mauritanian healthcare system.',
    'about.history.title': 'Our History',
    'about.history.p1': 'Since its creation in 2017, LPM has experienced remarkable growth thanks to a clear strategic vision, a dedicated team, and a constant commitment to the quality of services offered. The agency has gradually established a structured organization with an active network of medical representatives covering the entire Mauritanian territory. It has formed partnerships with renowned laboratories, ensuring the representation of innovative and essential products for the national healthcare system.',
    'about.history.p2': 'Thanks to its positioning as a reliable intermediary between international laboratories, wholesalers, and health authorities, LPM has established itself as a key player in the pharmaceutical sector. Its ability to support regulatory files, promote products with scientific rigor, and ensure effective field follow-up constitutes its main strength today.',
    'about.history.p3': 'Today, Leader ProMed is recognized for its professionalism, dynamism, and central role in the development of pharmaceutical investments in Mauritania.',
    'about.values.title': 'Our Values',
    'about.excellence.title': 'Excellence',
    'about.excellence.desc': 'We are committed to maintaining the highest quality standards in all our products and services.',
    'about.innovation.title': 'Innovation',
    'about.innovation.desc': 'We stay at the forefront of medical technology to offer the most advanced solutions.',
    'about.trust.title': 'Trust',
    'about.trust.desc': 'We build lasting relationships based on transparency and reliability.',
    'about.team.title': 'Our Team',
    'about.team.p1': 'The success of Leader ProMed relies primarily on the strength of its team. Behind every partnership, every promoted product, every supported file, there is a collective of committed, competent professionals passionate about healthcare innovation.',
    
    // Services page
    'services.medical.title': 'Medical Equipment',
    'services.medical.desc': 'Wide range of latest generation medical equipment for all types of healthcare facilities.',
    'services.cardiology.title': 'Cardiology',
    'services.cardiology.desc': 'Specialized solutions for the diagnosis and treatment of cardiovascular pathologies.',
    'services.maintenance.title': 'Maintenance & Support',
    'services.maintenance.desc': 'Complete after-sales service with preventive and corrective maintenance of your equipment.',
    'services.innovation.title': 'Innovative Solutions',
    'services.innovation.desc': 'Cutting-edge technologies to improve the efficiency and quality of care.',
    'services.process.title': 'Our Process',
    'services.consultation': 'Consultation',
    'services.consultation.desc': 'Analysis of your specific needs',
    'services.proposition': 'Proposition',
    'services.proposition.desc': 'Personalized solutions and detailed quote',
    'services.installation': 'Installation',
    'services.installation.desc': 'Setup and configuration',
    'services.support': 'Support',
    'services.support.desc': 'Ongoing support and maintenance',
    'services.learn.more': 'Learn more',
    // Services cards
    'services.card1.title': 'Laboratory Representation',
    'services.card1.description': 'We represent international pharmaceutical laboratories in Mauritania, ensuring a professional and effective presence in the local market.',
    'services.card1.detail1': 'Exclusive representation of international laboratories',
    'services.card1.detail2': 'Managing relations with local authorities',
    'services.card1.detail3': 'Monitoring commercial activities',
    'services.card1.detail4': 'Organizing scientific events',
    'services.card1.detail5': 'Training promotion teams',
    'services.card2.title': 'Drug Promotion',
    'services.card2.description': 'Our medical promotion team maintains an active presence with healthcare professionals to promote our partners\' products.',
    'services.card2.detail1': 'Regular medical visits',
    'services.card2.detail2': 'Organizing scientific meetings',
    'services.card2.detail3': 'Ongoing training for healthcare professionals',
    'services.card2.detail4': 'Monitoring prescriptions',
    'services.card2.detail5': 'Market trend analysis',
    'services.card3.title': 'Drug Registration',
    'services.card3.description': 'We support and monitor the registration of drug files for obtaining CLV or AMM from the competent authorities.',
    'services.card3.detail1': 'Preparation of registration files',
    'services.card3.detail2': 'Complete administrative follow-up',
    'services.card3.detail3': 'Coordination with health authorities',
    'services.card3.detail4': 'Renewal management',
    'services.card3.detail5': 'Regulatory advice',
    'services.card4.title': 'Pharmacovigilance',
    'services.card4.description': 'We manage pharmacovigilance to ensure drug safety and monitor potential adverse effects.',
    'services.card4.detail1': 'Collection and analysis of adverse effects',
    'services.card4.detail2': 'Periodic safety reports',
    'services.card4.detail3': 'Management of health alerts',
    'services.card4.detail4': 'Pharmacovigilance training',
    'services.card4.detail5': 'Regulatory compliance',
    'services.card5.title': 'Tender Management',
    'services.card5.description': 'We assist our partners in managing and responding to tenders in Mauritania, with local market expertise.',
    'services.card5.detail1': 'Tender monitoring',
    'services.card5.detail2': 'Preparation of response files',
    'services.card5.detail3': 'Analysis of specifications',
    'services.card5.detail4': 'Contract negotiation',
    'services.card5.detail5': 'Market follow-up',
    'services.card6.title': 'Investment Consulting',
    'services.card6.description': 'We offer consulting services for laboratories interested in investing in Mauritania, with local market expertise.',
    'services.card6.detail1': 'Detailed market study',
    'services.card6.detail2': 'Regulation analysis',
    'services.card6.detail3': 'Identification of opportunities',
    'services.card6.detail4': 'Market entry strategy',
    'services.card6.detail5': 'Support for establishment',
    'services.card7.title': 'Medical Equipment',
    'services.card7.description': 'We provide high-quality medical equipment for healthcare facilities in Mauritania.',
    'services.card7.detail1': 'Selection of quality equipment',
    'services.card7.detail2': 'Installation and training',
    'services.card7.detail3': 'Preventive maintenance',
    'services.card7.detail4': 'Technical support',
    'services.card7.detail5': 'Equipment upgrade',
    // Footer
    'footer.description': 'Your trusted partner for medical promotion in Mauritania since 2017.',
    'footer.quickLinks': 'Quick Links',
    'footer.link.home': 'Home',
    'footer.link.about': 'About',
    'footer.link.services': 'Services',
    'footer.link.partners': 'Partners',
    'footer.link.news': 'News',
    'footer.link.contact': 'Contact',
    'footer.contact': 'Contact',
    'footer.address': 'ZRB NO 0304, Nouakchott, Mauritania',
    'footer.phone': '+222 41 41 47 67',
    'footer.email': 'contact@leaderpromed.com',
    'footer.rights': 'All rights reserved.',
    // Home partners
    'home.partners.title': 'Our Partners',
    'home.partners.desc': 'We work with the most reputable international pharmaceutical laboratories to offer quality products to the Mauritanian market.',
    // Home news
    'home.news.title': 'News & Events',
    'home.news.desc': 'Stay informed about our latest news and events in the medical sector.',
    'home.news.card1.title': "Scientific Meeting on Diabetic Foot",
    'home.news.card1.content': "The Mauritanian Association of Endocrine Diseases, Diabetes and Nutrition organized a scientific meeting entitled 'The Diabetic Foot: from Prevention to Treatment of Complications'. This conference brought together medical experts to discuss best practices for prevention and treatment of diabetic foot in Mauritania.",
    'home.news.card2.title': "4th Maghreb Congress of Orthopedic Surgery",
    'home.news.card2.content': "We share with you some moments from the first day of the 4th Maghreb Congress - National Congress of Orthopedic and Traumatological Surgery, organized by the Mauritanian Society of Orthopedic and Traumatological Surgery. This major event brings together the best specialists from the Maghreb to discuss the latest advances in orthopedic surgery.",
    'home.news.card3.title': "2nd National Congress of Mauritanian Doctors",
    'home.news.card3.content': "Highlights from the 2nd National Congress of the National Order of Mauritanian Doctors, which was held yesterday and continues today, have been captured to keep a living record of this major meeting. This event brings together the elite of the Mauritanian medical profession to discuss current issues in the field.",
    'home.news.cta': 'All News',
    // Home gallery
    'home.gallery.title': 'Gallery',
    'home.gallery.desc': 'Discover our events, trainings and activities in pictures.',
    'home.gallery.slide1.title': "Orthopedics Congress - Session 1",
    'home.gallery.slide1.date': 'March 2024',
    'home.gallery.slide2.title': "Orthopedics Congress - Session 2",
    'home.gallery.slide2.date': 'March 2024',
    'home.gallery.slide3.title': "Orthopedics Congress - Session 3",
    'home.gallery.slide3.date': 'March 2024',
    'home.gallery.slide4.title': "SOMEDIAN Congress - Session 1",
    'home.gallery.slide4.date': 'March 2024',
    'home.gallery.slide5.title': "SOMEDIAN Congress - Session 2",
    'home.gallery.slide5.date': 'March 2024',
    'home.gallery.slide6.title': "SOMEDIAN Congress - Session 3",
    'home.gallery.slide6.date': 'March 2024',
    // Home contact
    'home.contact.title': 'Contact Us',
    'home.contact.subtitle': 'We are always available to answer your questions and needs.',
    'home.contact.form.title': 'Send us a message',
    'home.contact.info.title': 'Contact Information',
    'home.contact.info.address': 'Address',
    'home.contact.info.email': 'Email',
    'home.contact.info.phone': 'Phone',
    'home.contact.info.follow': 'Follow us',
    'home.whoWeAre.title': 'Who We Are',
    'home.whoWeAre.p1': "Leader ProMed (LPM) is a <about>pharmaceutical investment development agency</about> in Mauritania. It acts as a strategic intermediary between <partners>international pharmaceutical laboratories</partners> and authorized wholesalers in Mauritania, while facilitating relations with the Ministry of Health.",
    'home.whoWeAre.p2': "With a team of qualified professionals and in-depth knowledge of the health sector in Mauritania, we offer <services>customized services</services> to meet the specific needs of our partners and contribute to improving healthcare in the country.",
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('fr');

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
